import ControlGroup from "./control-group";
import React, {useContext} from "react";
import {FormContext} from "../../index";
import classNames from "classnames";
import {prefixExpression} from "../utils";
import _cloneDeep from "lodash/cloneDeep";
import _set from "lodash/set";

ActionInput.propTypes = {};

ActionInput.defaultProps = {
    onChange: () => {},
};

// Strips HTML tags and normalizes whitespace
export const cleanLabel = (string) => {
    if (!string) return '';
    const doc = new DOMParser().parseFromString(string, "text/html");
    return doc.documentElement.textContent.trim();
};

// Merges default field config with optional schema-based overrides from props.fieldOptions.
// If type: "hidden" is specified, the field will be completely excluded from rendering.
// Example: To override the 'label' field, pass:
// fieldOptions: {
//   label: {
//     required: false,
//     label: "CTA Text"
//   }
// }
// To completely remove a field from rendering, set: fieldOptions: { label: { hidden: true } }
// This is different from using type: \"hidden\", which still renders a hidden input.
export const buildField = (key, defaults, overrides, basePath) => {
    const override = overrides?.[key];
    if (override?.hidden === true) return null;

    const merged = {
        ...defaults,
        ...(override || {})
    };

    if (typeof merged.showsWhen === 'string') {
        merged.showsWhen = prefixExpression(merged.showsWhen, basePath);
    }

    if (key === "type" && override?.options && Array.isArray(override.options)) {
        const base = defaults.options ?? [];
        const ov = override.options;

        const keepAll = ov.some(o => o === '*');

        const filterValues = ov.filter(o => typeof o === 'string' && o !== '*');
        const filtered = keepAll
            ? base
            : (filterValues.length
                ? base.filter(b => filterValues.includes(b.value))
                : base);

        const extras = ov
            .filter(o => typeof o === 'object' && o && 'value' in o)
            .filter(o => !filtered.some(b => b.value === o.value));

        merged.options = [...filtered, ...extras];
    }

    return merged;
};

const getFields = (props, basePath, fieldKey, formContext) => {
    const overrides = props.fieldOptions || {};

    const blocks = Array.isArray(formContext?.selectedNode?.components)
        ? formContext.selectedNode.components.map((c) => ({
            label: c?.values?.title ? cleanLabel(c.values.title) : c?.label,
            value: c?.id
        }))
        : [];

    const channelId = formContext?.hrefUriParams?.channelId || formContext?.websiteManagementContext?.channel?.code;

    const allActionTypes = [
        channelId ? {label: "Open page", value: "page"} : null,
        {label: "Open link", value: "link"},
        {label: "Download file", value: "file"},
        blocks.length ? {label: "Scroll to block", value: "block"} : null,
        {label: "Open Product Page", value: "product"},
        {label: "View Product Checkout", value: "checkout"},
        {label: "View Product Category", value: "category"}
    ].filter(Boolean);

    const typeOverrideOpts = overrides?.type?.options;
    const filteredActionTypes = Array.isArray(typeOverrideOpts)
        ? (typeOverrideOpts.some(o => o === '*' || (typeof o === 'object' && o && 'value' in o))
            ? allActionTypes
            : allActionTypes.filter(o => typeOverrideOpts.includes(o.value)))
        : allActionTypes;


    return [
        buildField("showingActionButton", {
            label: props.label || "Show Action Button",
            type: "switch",
            name: basePath ? `${basePath}.showingActionButton` : `showingActionButton`,
            flipLayout: true,
        }, overrides, basePath),

        buildField("label", {
            label: "Button Label",
            type: "text",
            name: `${props.name}.label`,
            showsWhen: `showingActionButton === true`,
            required: true,
        }, overrides, basePath),

        buildField("type", {
            label: "Action Type",
            type: "select",
            onChange: (e, name) => {
                const selectedType = e?.target?.__cx__?.value;
                const basePath = props.name;

                // Clone current values
                const updatedComponent = _cloneDeep(formContext.values);

                // Set new type
                _set(updatedComponent, `${basePath}.type`, selectedType);

                // Clear stale fields
                _set(updatedComponent, `${basePath}.value`, undefined);
                _set(updatedComponent, `${basePath}.uniqueUri`, undefined);
                _set(updatedComponent, `${basePath}.productUuid`, undefined);

                // Trigger proper form update
                formContext.setContext(prev => ({
                    ...prev,
                    values: updatedComponent
                }));
            },
            name: `${props.name}.type`,
            options: filteredActionTypes,
            showsWhen: `showingActionButton === true`,
            required: true,
        }, overrides, basePath),

        buildField("category", {
            label: "Category",
            type: "combobox",
            name: `${props.name}.uniqueUri`,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'category'`,
            href: "/api/v2/categories?all=true",
            required: true,
            pageable: false,
            pageSize: 10000,
            canCreate: false,
            labelFieldPath: "category",
            valueFieldPath: "id",
            fuzzySearch: true,
            onLoad: "const allOptions = [];(function getOptions(options) {for (const o of options) {const option = o.original ?? o;allOptions.push({label: option?.category?.replace(\/\\\/\\\/\\\/\/g, ' > '), value: option.id});if(option.options?.length > 0) getOptions(option.options);}})(options);return allOptions;"
        }, overrides, basePath),

        buildField("file", {
            label: "File",
            type: "document-uploader",
            description: "Maximumfile : 100MB",
            name: `${props.name}.value`,
            required: true,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'file'`
        }, overrides, basePath),

        buildField("page", {
            label: "Page",
            type: "combobox",
            name: `${props.name}.uniqueUri`,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'page'`,
            href: `/api/v2/channels/${channelId}/search/pages`,
            required: true,
            pageable: true,
            fuzzySearch: true,
            canCreate: false
        }, overrides, basePath),

        buildField("block", {
            label: "Block",
            type: "combobox",
            name: `${props.name}.value`,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'block'`,
            options: blocks,
            required: true,
            pageable: true,
            fuzzySearch: true,
            canCreate: false
        }, overrides, basePath),

        buildField("product", {
            label: "Product",
            type: "combobox",
            name: `${props.name}.productUuid`,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'checkout' || ${fieldKey}.type === 'product' && showingActionButton === true`,
            href: "/api/v2/search/products",
            required: true,
            pageable: true,
            pageSize: 10000,
            canCreate: false,
            labelFieldPath: "title",
            valueFieldPath: "id"
        }, overrides, basePath),

        buildField("link", {
            label: "Button URL",
            type: "text",
            name: `${props.name}.value`,
            showsWhen: `showingActionButton === true && ${fieldKey}.type === 'link'`,
            required: true
        }, overrides, basePath),

        buildField("target", {
            label: "Open in new tab",
            type: "switch",
            name: `${props.name}.target`,
            truthyValue: "_blank",
            falsyValue: "_self",
            showsWhen: `showingActionButton === true && ${fieldKey}.type !== undefined && ${fieldKey}.type !== null && ${fieldKey}.type !== '' && ${fieldKey}.type !== 'file'`,
            flipLayout: true,
        }, overrides, basePath),
    ].filter(Boolean);
};

export default function ActionInput(props) {
    const parts = props.name.split('.');
    const basePath = parts.slice(0, -1).join('.');
    const fieldKey = parts.at(-1);

    const formContext = useContext(FormContext);
    const fields = getFields(props, basePath, fieldKey, formContext);
    const showEdit = props.editing && !props.system;

    return (
        <div>
            {showEdit ? (
                <div className={classNames("block w-full")}>
                <span
                    className={"text-xs font-medium text-primary-500 leading-5 cursor-pointer hover:underline"}
                    onClick={props.onEditFieldSettings}
                >Edit</span>
                </div>
            ) : null}
            <ControlGroup {...props} className={"!space-y-5 !block"} fields={fields}/>
        </div>
    );
}